import { Autocomplete, Box } from "@mui/material";

import CancelButton from "Common/Components/Buttons/CancelButton";
import PrimarySubmitButton from "Common/Components/Buttons/PrimarySubmitButton";
import FileUpload from "Common/Components/FileUpload.tsx/FileUpload";
import BootstrapTextField from "Common/Components/InputComponent/BootstrapTextField";
import PageBreadcrumb from "Common/Components/PageBreadcrumb/PageBreadcrumb";
import { MecordColors } from "Constants/Color";
import { autocompleteDropdownStyles } from "Constants/Style";
import NewFileUploadInput from "components/FilePreview/FileInputPreview";
import LoadingSpinner from "components/LoadingSpinner";
import { useFormik } from "formik";
import { showError } from "helpers/swalHelper";
import React, { useEffect, useState } from "react";
import { Accordion, Col, Container, Form, Row } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { fetchPurchaseOrderId } from "slices/Procurement/purchase-order/purchase-order/thunk";
import Swal from "sweetalert2";
import * as Yup from "yup";
import CreateGRNTable from "./Components/CreateGRNTable";
import { createGRN } from "slices/Warehouse/ItemInward/GRN/thunk";
import { clearAllTableStates } from "utils/clearTableStates";
import { useScrollToFormikError } from "hooks/useScrollToFormikError";

interface GRNFormValues {
  po_id: string;
  received_date: string;
  received_by: string;

  supplier_id: string;

  transporter_type: string;
  transporter_name?: string;
  person_number?: string;
  docket_number?: string;
  company_name?: string;
  vehicle_number?: string;
  driver_name?: string;
  driver_number?: string;
  carrier_name?: string;
  train_number?: string;
  booking_number?: string;
  origin?: string;
  flight_number?: string;
  awb_number?: string;

  delivery_challan_no: string;
  upload_delivery_challan: File | string | null;
  lr_no: string;
  upload_lr_document: File | string | null;

  invoice_no: string;
  invoice_date: string;
  upload_invoice: File | string | null;

  notes: string;
  attachments: (File | string)[];
}

const WarehouseCreateGRN: React.FC = () => {
  const navigate = useNavigate();
  const { po_id, warehouse_id } = useParams();
  const dispatch = useDispatch<any>();
  const [isAddStockOpen, setIsAddStockOpen] = useState(false);
  const [poData, setPoData] = useState<any>({});
  const [products, setProducts] = useState<any[]>([]);
  const [pageLoading, setPageLoading] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const transportTypeOptions = [
    "Self Pickup",
    "Courier",
    "Road",
    "Rail",
    "Air",
  ];

  const pincodeRegExp = /^[1-9][0-9]{5}$/;
  // const vehicleRegExp = /^[A-Z]{2}\d{2}[A-Z]{2}\d{4}$/; // Basic Indian vehicle format
  const vehicleRegExp = /^[A-Z]{2}\s\d{2}\s[A-Z]{2}\s\d{4}$/;

  const validationSchema = Yup.object({
    po_id: Yup.string().required("Please enter PO No."),
    received_date: Yup.string().required("Please select Received Date"),
    received_by: Yup.string().trim().required("Please enter Received By"),

    transporter_type: Yup.string().required("Please select Transport Type"),
    transporter_name: Yup.string(),
    person_number: Yup.string(),
    docket_number: Yup.string(),
    company_name: Yup.string(),
    vehicle_number: Yup.string(),
    driver_name: Yup.string(),
    driver_number: Yup.string(),
    carrier_name: Yup.string(),
    train_number: Yup.string(),
    booking_number: Yup.string(),
    origin: Yup.string(),
    flight_number: Yup.string(),
    awb_number: Yup.string(),

    delivery_challan_no: Yup.string(),
    lr_no: Yup.string(),
    upload_delivery_challan: Yup.mixed<File | string>()
      .nullable()
      .notRequired(),
    upload_lr_document: Yup.mixed<File | string>().nullable().notRequired(),

    invoice_no: Yup.string().trim().required("Please enter Invoice No."),
    invoice_date: Yup.string().required("Please select Invoice Date"),
    upload_invoice: Yup.mixed<File | string>().nullable().notRequired(),

    notes: Yup.string(),
    attachments: Yup.mixed<File | string>().nullable().notRequired(),
  });

  const formik = useFormik<GRNFormValues>({
    initialValues: {
      po_id: po_id ? po_id : "",
      received_date: "",
      received_by: "",
      supplier_id: "",
      transporter_type: "",
      transporter_name: "",
      person_number: "",
      docket_number: "",
      company_name: "",
      vehicle_number: "",
      driver_name: "",
      driver_number: "",
      carrier_name: "",
      train_number: "",
      booking_number: "",
      origin: "",
      flight_number: "",
      awb_number: "",

      delivery_challan_no: "",
      upload_delivery_challan: null,

      lr_no: "",
      upload_lr_document: null,

      invoice_no: "",
      invoice_date: "",
      upload_invoice: null,

      notes: "",
      attachments: [],
    },

    validationSchema: validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: async (values, { setTouched, validateForm }) => {
      // Mark all fields as touched to show validation errors
      const allFields = Object.keys(values);
      const touchedFields: any = {};
      allFields.forEach((field) => {
        touchedFields[field] = true;
      });
      setTouched(touchedFields);

      // Validate the form
      const errors = await validateForm();

      // Check if there are validation errors
      if (Object.keys(errors).length > 0) {
        toast.error("Please fill all mandatory fields.");
        return;
      }

      // 1️⃣ Check if ALL items have no stock details
      const allMissingDetails = products.every(
        (item) => !item.grn_products || item.grn_products.length === 0,
      );

      if (allMissingDetails) {
        showError("Please add stock details for at least one item.");
        return;
      }

      // 2️⃣ Validate quantity vs stock details
      const hasMismatch = products.some((item, idx) => {
        const details = item.grn_products;

        // skip items with no stock details
        if (!details || details.length === 0) return false;

        const totalQuantity =
          (item.quantity || 0) + (item.damaged_quantity || 0);

        if (details.length !== totalQuantity) {
          showError(
            "",
            `Inward Quantity and Stock Quantity mismatch for Sr No. ${idx + 1}`,
          );
          return true; // stops `.some`
        }

        return false;
      });

      if (hasMismatch) return;

      const transporter_details: any = {};
      let hasTransporterDetails = false;

      if (values.transporter_type?.toLocaleLowerCase() === "self pickup") {
        transporter_details.transporter_name = values.transporter_name || "";
        transporter_details.phone_no = values.person_number || "";
        if (values.transporter_name || values.person_number)
          hasTransporterDetails = true;
      }
      if (values.transporter_type?.toLocaleLowerCase() === "courier") {
        transporter_details.docket_number = values.docket_number || "";
        transporter_details.company_name = values.company_name || "";
        if (values.docket_number || values.company_name)
          hasTransporterDetails = true;
      }
      if (values.transporter_type?.toLocaleLowerCase() === "road") {
        transporter_details.transporter_name = values.transporter_name || "";
        transporter_details.vehicle_number = values.vehicle_number || "";
        transporter_details.driver_name = values.driver_name || "";
        transporter_details.driver_number = values.driver_number || "";
        if (
          values.transporter_name ||
          values.vehicle_number ||
          values.driver_name ||
          values.driver_number
        )
          hasTransporterDetails = true;
      }
      if (values.transporter_type?.toLocaleLowerCase() === "rail") {
        transporter_details.carrier_name = values.carrier_name || "";
        transporter_details.train_number = values.train_number || "";
        transporter_details.booking_number = values.booking_number || "";
        transporter_details.origin = values.origin || "";
        if (
          values.carrier_name ||
          values.train_number ||
          values.booking_number ||
          values.origin
        )
          hasTransporterDetails = true;
      }
      if (values.transporter_type?.toLocaleLowerCase() === "air") {
        transporter_details.carrier_name = values.carrier_name || "";
        transporter_details.flight_number = values.flight_number || "";
        transporter_details.awb_number = values.awb_number || "";
        transporter_details.origin = values.origin || "";
        if (
          values.carrier_name ||
          values.flight_number ||
          values.awb_number ||
          values.origin
        )
          hasTransporterDetails = true;
      }

      setIsSubmitting(true);

      try {
        const formData = new FormData();

        // PO Details
        formData.append("po_id", values.po_id);
        formData.append("received_date", values.received_date);
        formData.append("received_by", values.received_by);

        // Supplier Details
        formData.append("supplier_id", poData.supplier_id);
        const supplier_details = {
          name: poData?.supplier_name,
          email: poData?.email,
          phone_number: poData?.phone_number,
          contact_person_name: poData?.contact_person_name,
          contact_person_email: poData?.contact_person_email,
          contact_person_phone_number: poData?.contact_person_phone_number,
        };

        formData.append("supplier_details", JSON.stringify(supplier_details));

        formData.append(
          "warehouse_id",
          warehouse_id || poData?.ship_to || poData?.warehouse_id,
        );

        // Supplier ID take from PO

        // Transport Details
        formData.append(
          "transporter_type",
          values.transporter_type?.toLowerCase(),
        );
        if (hasTransporterDetails) {
          formData.append(
            "transporter_details",
            JSON.stringify(transporter_details),
          );
        }

        // Other Documents (optional)
        formData.append("delivery_challan_no", values.delivery_challan_no);
        formData.append("lr_no", values.lr_no || "");
        formData.append("invoice_no", values.invoice_no);
        formData.append("invoice_date", values.invoice_date);
        formData.append("note", values.notes || "");

        // Add file uploads
        if (values.upload_delivery_challan) {
          formData.append(
            "delivery_challan_document",
            values.upload_delivery_challan,
          );
        }
        if (values.upload_lr_document) {
          formData.append("lr_document", values.upload_lr_document);
        }
        if (values.upload_invoice) {
          formData.append("invoice_document", values.upload_invoice);
        }
        // Multiple files with same name attachements []
        // if (values.attachments?.length) {
        //   values.attachments.forEach((file) => {
        //     formData.append("attachments", file);
        //   });
        // }

        if (values.attachments?.length) {
          values.attachments.forEach((item: any) => {
            const file =
              item instanceof File ? item : item.file || item.originFileObj;

            if (file instanceof File) {
              formData.append("attachments", file); // ✅ NOT attachments[]
            }
          });
        }

        console.log("FORMDATA : ", formData);

        // Prepare products array
        const filteredPoItems = products.filter(
          (item) => item.grn_products !== undefined,
        );

        const grnPoItems = filteredPoItems.map((item) => {
          const poProductId = item.po_product_id || item.id || "";
          return {
            po_product_id: poProductId,
            product_id: item.product_id || "",
            order_quantity: item.order_quantity || 0,
            received_quantity: item.received_quantity || 0,
            quantity: item.quantity || 0,
            damaged_quantity: item.damaged_quantity || 0,
            balance_quantity: item.balance_quantity || 0,
            remark: item.remark || "",
            grn_products: item.grn_products || [],
          };
        });

        formData.append("products", JSON.stringify(grnPoItems));

        // Dispatch the thunk
        const result = await dispatch(createGRN(formData)).unwrap();
        clearAllTableStates();

        toast.success("GRN created successfully");
        // Navigate to GRN listing tab (tab 2)

        navigate("/warehouse-item-inward");
      } catch (err: any) {
        console.error("Error creating GRN:", err);

        const backendMessage = err?.message || err?.response?.data?.message;
        const errType = err?.response?.data?.errors;

        if (typeof backendMessage === "string") {
          toast.error(backendMessage);
        } else if (errType && Object.keys(errType).length > 0) {
          Object.keys(errType).forEach((key: string) => {
            Swal.fire({
              icon: "error",
              text: `${errType[key][0]}`,
            });
          });
        } else {
          toast.error("Failed to create GRN");
        }
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  useScrollToFormikError(formik.errors, formik.submitCount);

  useEffect(() => {
    if (po_id) {
      setPageLoading(true);
      dispatch(fetchPurchaseOrderId(po_id))
        .unwrap()
        .then((data: any) => {
          setPoData(data?.data);
          const updated_products = data?.data?.products?.map((p: any) => ({
            ...p,
            actual_balance_quantity: p?.balance_quantity,
            received_quantity: "",
            quantity: "",
            damaged_quantity: 0,
            balance_quantity: p?.balance_quantity || 0,
          }));
          setProducts(updated_products);
        })
        .catch((err: any) => {
          toast.error(err?.message || "Failed to fetch Purchase Order details");
        })
        .finally(() => {
          setPageLoading(false);
        });
    }
  }, [dispatch, po_id]);

  console.log("products : ", products);
  return (
    <div
      className="page-content px-4"
      style={{ backgroundColor: "#e9ecef", minHeight: "100vh" }}
    >
      <style>
        {`
                        .accordion-terms-conditions .accordion-button,
                        .accordion-notes .accordion-button,
                        .accordion-attach-files .accordion-button {
                          background-color: ${MecordColors?.LightGrayColor} !important;
                          color: ${MecordColors?.NormalText} !important;
                        }
                        .accordion-terms-conditions .accordion-button:not(.collapsed),
                        .accordion-notes .accordion-button:not(.collapsed),
                        .accordion-attach-files .accordion-button:not(.collapsed) {
                          background-color: ${MecordColors?.LightGrayColor} !important;
                          color: ${MecordColors?.NormalText} !important;
                        }
                      `}
      </style>
      <Container
        fluid
        style={{
          backgroundColor: "#fff",
          padding: "30px",
          borderRadius: "10px",
          boxShadow: "0 0 15px rgba(0,0,0,0.1)",
        }}
      >
        {!isAddStockOpen ? (
          <>
            <PageBreadcrumb
              title={false ? "Edit GRN" : "Create GRN"}
              titleLink="/warehouse-item-inward"
              breadcrumbs={[
                {
                  label: "Item Inward",
                  to: "/warehouse-item-inward",
                },
                {
                  label: false ? "Edit GRN" : "Create GRN",
                  to: "",
                },
              ]}
              backNavigationPath={-1}
            />
            <hr />
          </>
        ) : (
          <>
            <PageBreadcrumb
              title={true ? "Add Stock" : "Edit Stock"}
              breadcrumbs={[
                {
                  label: "Create GRN",
                  to: "",
                },
                {
                  label: true ? "Add Stock" : "Edit Stock",
                  to: "",
                },
              ]}
              isBackArrow={false}
              // backNavigationPath={-1}
            />
            <hr />
          </>
        )}

        {pageLoading ? (
          <LoadingSpinner />
        ) : (
          <Form autoComplete="off" onSubmit={formik.handleSubmit}>
            {!isAddStockOpen && (
              <>
                {/* Inward & Supplier Details */}
                <Row className="mb-4 g-4">
                  {/* Inward Details */}
                  <Col lg={6} md={12}>
                    <h5 className="fw-semibold mb-3">Purchase Order Details</h5>
                    <div
                      className="p-3 rounded mb-3"
                      style={{ backgroundColor: MecordColors?.LightGrayColor }}
                    >
                      <Row className="g-3">
                        {/* PO No */}
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label>
                              PO No. <span className="text-danger">*</span>
                            </Form.Label>

                            <Form.Control
                              type="text"
                              name="po_no"
                              placeholder="Enter PO Number"
                              value={poData?.po_no}
                              readOnly
                            />
                          </Form.Group>
                        </Col>

                        {/* Initiated By */}
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label>
                              Initiated By{" "}
                              <span className="text-danger">*</span>
                            </Form.Label>

                            <Form.Control
                              type="text"
                              readOnly
                              name="initiated_by"
                              placeholder="Enter Name"
                              value={poData?.initiated_by}
                            />
                          </Form.Group>
                        </Col>

                        {/* PO Date */}
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label>
                              PO Date <span className="text-danger">*</span>
                            </Form.Label>

                            <Form.Control
                              type="date"
                              name="po_date"
                              autoComplete="off"
                              value={poData?.po_date?.split("T")?.[0]}
                              readOnly
                            />
                          </Form.Group>
                        </Col>

                        {/* Received Date */}
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label>
                              Received Date{" "}
                              <span className="text-danger">*</span>
                            </Form.Label>

                            <Form.Control
                              type="date"
                              name="received_date"
                              autoComplete="off"
                              value={formik.values.received_date}
                              min={poData?.po_date?.split("T")?.[0]}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              isInvalid={
                                formik.touched.received_date &&
                                !!formik.errors.received_date
                              }
                            />

                            <Form.Control.Feedback type="invalid">
                              {formik.errors.received_date}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Col>

                        {/* Received By */}
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label>
                              Received By <span className="text-danger">*</span>
                            </Form.Label>

                            <Form.Control
                              type="text"
                              name="received_by"
                              placeholder="Enter Name"
                              value={formik.values.received_by}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              isInvalid={
                                formik.touched.received_by &&
                                !!formik.errors.received_by
                              }
                            />

                            <Form.Control.Feedback type="invalid">
                              {formik.errors.received_by}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                      </Row>
                    </div>
                  </Col>

                  {/* Company/Customer Details - RIGHT COLUMN */}
                  <Col lg={6} md={12}>
                    <div className="d-flex justify-content-between align-items-center">
                      <h5 className=" d-flex justify-content-center align-items-center fw-semibold mb-3 me-15">
                        Supplier Details
                      </h5>
                    </div>
                    <div
                      className="p-3 rounded mb-3"
                      style={{
                        background: MecordColors.ActiveBackground,
                      }}
                    >
                      <Row className="g-3">
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label>
                              Supllier <span className="text-danger">*</span>
                            </Form.Label>

                            <Form.Control
                              type="text"
                              placeholder="Autofill"
                              value={poData?.supplier_name}
                              readOnly
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label>
                              Company Email{" "}
                              <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                              type="text"
                              placeholder="Autofill"
                              value={poData?.email || ""}
                              readOnly
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label>
                              Company Phone Number{" "}
                              <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                              type="text"
                              placeholder="Autofill"
                              value={poData?.phone_number || ""}
                              readOnly
                            />
                          </Form.Group>
                        </Col>

                        <Col md={6}>
                          <Form.Group>
                            <Form.Label>
                              Contact Person Name{" "}
                              <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                              type="text"
                              placeholder="Autofill"
                              value={poData?.contact_person_name}
                              readOnly
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label>
                              Contact Person Email{" "}
                              <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                              type="text"
                              placeholder="Autofill"
                              value={poData?.contact_person_email}
                              readOnly
                            />
                          </Form.Group>
                        </Col>

                        <Col md={6}>
                          <Form.Group>
                            <Form.Label>
                              Contact Person Phone Number{" "}
                              <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                              type="text"
                              placeholder="Autofill"
                              value={poData?.contact_person_phone_number}
                              readOnly
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                    </div>
                  </Col>
                </Row>

                <hr />

                {/* Transporter Details */}
                <Row className="mb-3 g-4">
                  <h5 className="fw-semibold">Transporter Details</h5>
                  <div
                    className="p-3 ms-3 rounded"
                    style={{ backgroundColor: MecordColors.ActiveBackground }}
                  >
                    <Row>
                      <Col lg={3}>
                        <div className="mb-3">
                          <Form.Label>
                            Transport Type{" "}
                            <span className="text-danger">*</span>
                          </Form.Label>
                          <Autocomplete
                            options={transportTypeOptions}
                            getOptionLabel={(option: any) =>
                              typeof option === "string" ? option : ""
                            }
                            value={formik.values.transporter_type || null}
                            onChange={(event, selected) => {
                              const value = selected || "";
                              // Set the value and trigger validation
                              formik.setFieldValue(
                                "transporter_type",
                                value,
                                true,
                              );

                              // Clear other transport-related fields
                              formik.setFieldValue("transporter_name", "");
                              formik.setFieldValue("person_number", "");
                              formik.setFieldValue("docket_number", "");
                              formik.setFieldValue("company_name", "");
                              formik.setFieldValue("vehicle_number", "");
                              formik.setFieldValue("driver_name", "");
                              formik.setFieldValue("driver_number", "");
                              formik.setFieldValue("carrier_name", "");
                              formik.setFieldValue("train_number", "");
                              formik.setFieldValue("booking_number", "");
                              formik.setFieldValue("origin", "");
                              formik.setFieldValue("flight_number", "");
                              formik.setFieldValue("awb_number", "");
                            }}
                            onBlur={() => {
                              formik.setFieldTouched("transporter_type", true);
                            }}
                            renderOption={(props, option, { selected }) => (
                              <li {...props} style={{ padding: 0 }}>
                                <Box
                                  sx={{
                                    px: 1.5,
                                    py: 0.8,
                                    width: "100%",
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    cursor: "pointer",

                                    // ✅ Hover state
                                    "&:hover": {
                                      backgroundColor:
                                        MecordColors.Hover.backgroundColor,
                                      color: MecordColors.ActiveText,
                                    },

                                    // ✅ Selected state
                                    ...(selected && {
                                      backgroundColor:
                                        MecordColors.ActiveBackground,
                                      color: MecordColors.ActiveText,
                                    }),
                                  }}
                                >
                                  {/* Option Label */}
                                  <span>
                                    {typeof option === "string"
                                      ? option
                                      : String(option)}
                                  </span>
                                </Box>
                              </li>
                            )}
                            ListboxProps={
                              autocompleteDropdownStyles.ListboxProps
                            }
                            renderInput={(params) => (
                              <BootstrapTextField
                                {...params}
                                name="transporter_type"
                                placeholder="Select Transport Type"
                                error={
                                  formik.touched.transporter_type &&
                                  Boolean(formik.errors.transporter_type) &&
                                  !formik.values.transporter_type
                                }
                                helperText={
                                  formik.touched.transporter_type &&
                                  !formik.values.transporter_type &&
                                  formik.errors.transporter_type
                                }
                                sx={{
                                  ...autocompleteDropdownStyles.inputSx,
                                }}
                                fullWidth
                                size="small"
                              />
                            )}
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                "& fieldset": {
                                  borderColor: "#ced4da",
                                },
                                "&:hover fieldset": {
                                  borderColor: "#ced4da",
                                },
                                "&.Mui-focused fieldset": {
                                  borderColor: "#ced4da",
                                },
                              },
                            }}
                          />
                        </div>
                      </Col>

                      {formik.values.transporter_type === "Self Pickup" && (
                        <>
                          <Col lg={3}>
                            <div className="mb-3">
                              <Form.Label>Transporter Name </Form.Label>
                              <Form.Control
                                type="text"
                                name="transporter_name"
                                placeholder="Enter Transporter Name"
                                value={formik.values.transporter_name}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                isInvalid={
                                  !!formik.errors.transporter_name &&
                                  formik.touched.transporter_name
                                }
                              />
                              <Form.Control.Feedback type="invalid">
                                {formik.errors.transporter_name}
                              </Form.Control.Feedback>
                            </div>
                          </Col>

                          <Col lg={3}>
                            <div className="mb-3">
                              <Form.Label>Person Number</Form.Label>
                              <Form.Control
                                type="text"
                                name="person_number"
                                placeholder="Enter Person Number"
                                value={formik.values.person_number}
                                // onChange={formik.handleChange}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  if (/^\d*$/.test(value)) {
                                    formik.setFieldValue(
                                      "person_number",
                                      value,
                                    );
                                  }
                                }}
                                onBlur={formik.handleBlur}
                                isInvalid={
                                  !!formik.errors.person_number &&
                                  formik.touched.person_number
                                }
                              />
                              <Form.Control.Feedback type="invalid">
                                {formik.errors.person_number}
                              </Form.Control.Feedback>
                            </div>
                          </Col>
                        </>
                      )}

                      {formik.values.transporter_type === "Courier" && (
                        <>
                          <Col lg={3}>
                            <div className="mb-3">
                              <Form.Label>Docket Number</Form.Label>
                              <Form.Control
                                type="text"
                                name="docket_number"
                                placeholder="Enter Docket Number"
                                value={formik.values.docket_number}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                isInvalid={
                                  !!formik.errors.docket_number &&
                                  formik.touched.docket_number
                                }
                              />
                              <Form.Control.Feedback type="invalid">
                                {formik.errors.docket_number}
                              </Form.Control.Feedback>
                            </div>
                          </Col>
                          <Col lg={3}>
                            <div className="mb-3">
                              <Form.Label>Company Name</Form.Label>
                              <Form.Control
                                type="text"
                                name="company_name"
                                placeholder="Enter Company Name"
                                value={formik.values.company_name}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                isInvalid={
                                  !!formik.errors.company_name &&
                                  formik.touched.company_name
                                }
                              />
                              <Form.Control.Feedback type="invalid">
                                {formik.errors.company_name}
                              </Form.Control.Feedback>
                            </div>
                          </Col>
                        </>
                      )}

                      {formik.values.transporter_type === "Road" && (
                        <>
                          <Col lg={3}>
                            <div className="mb-3">
                              <Form.Label>Transporter Name </Form.Label>
                              <Form.Control
                                type="text"
                                name="transporter_name"
                                placeholder="Enter Transporter Name"
                                value={formik.values.transporter_name}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                isInvalid={
                                  !!formik.errors.transporter_name &&
                                  formik.touched.transporter_name
                                }
                              />
                              <Form.Control.Feedback type="invalid">
                                {formik.errors.transporter_name}
                              </Form.Control.Feedback>
                            </div>
                          </Col>

                          <Col lg={3}>
                            <div className="mb-3">
                              <Form.Label>Vehicle Number </Form.Label>
                              <Form.Control
                                type="text"
                                name="vehicle_number"
                                placeholder="Enter Vehicle Number"
                                value={formik.values.vehicle_number}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                isInvalid={
                                  !!formik.errors.vehicle_number &&
                                  formik.touched.vehicle_number
                                }
                              />
                              <Form.Control.Feedback type="invalid">
                                {formik.errors.vehicle_number}
                              </Form.Control.Feedback>
                            </div>
                          </Col>

                          <Col lg={3}>
                            <div className="mb-3">
                              <Form.Label>Driver Name</Form.Label>
                              <Form.Control
                                type="text"
                                name="driver_name"
                                placeholder="Enter Driver Name"
                                value={formik.values.driver_name}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                isInvalid={
                                  !!formik.errors.driver_name &&
                                  formik.touched.driver_name
                                }
                              />
                              <Form.Control.Feedback type="invalid">
                                {formik.errors.driver_name}
                              </Form.Control.Feedback>
                            </div>
                          </Col>

                          <Col lg={3}>
                            <div className="mb-3">
                              <Form.Label>Driver Number</Form.Label>
                              <Form.Control
                                type="text"
                                name="driver_number"
                                placeholder="Enter Driver Number"
                                value={formik.values.driver_number}
                                // onChange={formik.handleChange}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  if (/^\d*$/.test(value)) {
                                    formik.setFieldValue(
                                      "driver_number",
                                      value,
                                    );
                                  }
                                }}
                                onBlur={formik.handleBlur}
                                isInvalid={
                                  !!formik.errors.driver_number &&
                                  formik.touched.driver_number
                                }
                              />
                              <Form.Control.Feedback type="invalid">
                                {formik.errors.driver_number}
                              </Form.Control.Feedback>
                            </div>
                          </Col>
                        </>
                      )}

                      {formik.values.transporter_type === "Rail" && (
                        <>
                          <Col lg={3}>
                            <div className="mb-3">
                              <Form.Label>Carrier Name</Form.Label>
                              <Form.Control
                                type="text"
                                name="carrier_name"
                                placeholder="Enter Carrier Name"
                                value={formik.values.carrier_name}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                isInvalid={
                                  !!formik.errors.carrier_name &&
                                  formik.touched.carrier_name
                                }
                              />
                              <Form.Control.Feedback type="invalid">
                                {formik.errors.carrier_name}
                              </Form.Control.Feedback>
                            </div>
                          </Col>

                          <Col lg={3}>
                            <div className="mb-3">
                              <Form.Label>Train Number</Form.Label>
                              <Form.Control
                                type="text"
                                name="train_number"
                                placeholder="Enter Train Number"
                                value={formik.values.train_number}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                isInvalid={
                                  !!formik.errors.train_number &&
                                  formik.touched.train_number
                                }
                              />
                              <Form.Control.Feedback type="invalid">
                                {formik.errors.train_number}
                              </Form.Control.Feedback>
                            </div>
                          </Col>

                          <Col lg={3}>
                            <div className="mb-3">
                              <Form.Label>Booking Number </Form.Label>
                              <Form.Control
                                type="text"
                                name="booking_number"
                                placeholder="Enter Booking Number"
                                value={formik.values.booking_number}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                isInvalid={
                                  !!formik.errors.booking_number &&
                                  formik.touched.booking_number
                                }
                              />
                              <Form.Control.Feedback type="invalid">
                                {formik.errors.booking_number}
                              </Form.Control.Feedback>
                            </div>
                          </Col>

                          <Col lg={3}>
                            <div className="mb-3">
                              <Form.Label>Origin</Form.Label>
                              <Form.Control
                                type="text"
                                name="origin"
                                placeholder="Enter Origin"
                                value={formik.values.origin}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                isInvalid={
                                  !!formik.errors.origin &&
                                  formik.touched.origin
                                }
                              />
                              <Form.Control.Feedback type="invalid">
                                {formik.errors.origin}
                              </Form.Control.Feedback>
                            </div>
                          </Col>
                        </>
                      )}

                      {formik.values.transporter_type === "Air" && (
                        <>
                          <Col lg={3}>
                            <div className="mb-3">
                              <Form.Label>Carrier Name</Form.Label>
                              <Form.Control
                                type="text"
                                name="carrier_name"
                                placeholder="Enter Carrier Name"
                                value={formik.values.carrier_name}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                isInvalid={
                                  !!formik.errors.carrier_name &&
                                  formik.touched.carrier_name
                                }
                              />
                              <Form.Control.Feedback type="invalid">
                                {formik.errors.carrier_name}
                              </Form.Control.Feedback>
                            </div>
                          </Col>

                          <Col lg={3}>
                            <div className="mb-3">
                              <Form.Label>Flight Number</Form.Label>
                              <Form.Control
                                type="text"
                                name="flight_number"
                                placeholder="Enter Flight Number"
                                value={formik.values.flight_number}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                isInvalid={
                                  !!formik.errors.flight_number &&
                                  formik.touched.flight_number
                                }
                              />
                              <Form.Control.Feedback type="invalid">
                                {formik.errors.flight_number}
                              </Form.Control.Feedback>
                            </div>
                          </Col>

                          <Col lg={3}>
                            <div className="mb-3">
                              <Form.Label>AWB Number</Form.Label>
                              <Form.Control
                                type="text"
                                name="awb_number"
                                placeholder="Enter AWB Number"
                                value={formik.values.awb_number}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                isInvalid={
                                  !!formik.errors.awb_number &&
                                  formik.touched.awb_number
                                }
                              />
                              <Form.Control.Feedback type="invalid">
                                {formik.errors.awb_number}
                              </Form.Control.Feedback>
                            </div>
                          </Col>

                          <Col lg={3}>
                            <div className="mb-3">
                              <Form.Label>Origin</Form.Label>
                              <Form.Control
                                type="text"
                                name="origin"
                                placeholder="Enter Origin"
                                value={formik.values.origin}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                isInvalid={
                                  !!formik.errors.origin &&
                                  formik.touched.origin
                                }
                              />
                              <Form.Control.Feedback type="invalid">
                                {formik.errors.origin}
                              </Form.Control.Feedback>
                            </div>
                          </Col>
                        </>
                      )}
                    </Row>
                  </div>
                </Row>

                <hr />

                {/* LOGISTICS */}
                <Row className="mb-3 g-4">
                  <h5 className="fw-semibold mb-3">Logistics Details</h5>

                  <Col lg={3}>
                    <Form.Label>Delivery Challan No.</Form.Label>
                    <Form.Control
                      type="text"
                      name="delivery_challan_no"
                      placeholder="Enter Delivery Challan No."
                      value={formik.values.delivery_challan_no}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      isInvalid={
                        !!formik.errors.delivery_challan_no &&
                        formik.touched.delivery_challan_no
                      }
                    />
                    <Form.Control.Feedback type="invalid">
                      {formik.errors.delivery_challan_no}
                    </Form.Control.Feedback>
                  </Col>
                  <Col lg={3}>
                    <NewFileUploadInput
                      label="Upload Delivery Challan"
                      name="upload_delivery_challan"
                      formik={formik}
                    />
                  </Col>
                  <Col lg={3}>
                    <Form.Label>LR No.</Form.Label>
                    <Form.Control
                      type="text"
                      name="lr_no"
                      placeholder="Enter LR No."
                      value={formik.values.lr_no}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      isInvalid={!!formik.errors.lr_no && formik.touched.lr_no}
                    />
                    <Form.Control.Feedback type="invalid">
                      {formik.errors.lr_no}
                    </Form.Control.Feedback>
                  </Col>
                  <Col lg={3}>
                    <NewFileUploadInput
                      label="Upload LR Document"
                      name="upload_lr_document"
                      formik={formik}
                    />
                  </Col>
                </Row>

                <hr />

                {/* Invoice */}
                <Row className="mb-3 g-4">
                  <h5 className="fw-semibold mb-3">Invoice Details</h5>
                  <Col lg={3}>
                    <Form.Label>
                      Invoice No. <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="invoice_no"
                      placeholder="Enter Invoice No."
                      value={formik.values.invoice_no}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      isInvalid={
                        !!formik.errors.invoice_no && formik.touched.invoice_no
                      }
                    />
                    <Form.Control.Feedback type="invalid">
                      {formik.errors.invoice_no}
                    </Form.Control.Feedback>
                  </Col>
                  <Col lg={3}>
                    <Form.Label>
                      Invoice Date <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="date"
                      name="invoice_date"
                      value={formik.values.invoice_date}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      isInvalid={
                        !!formik.errors.invoice_date &&
                        formik.touched.invoice_date
                      }
                    />
                    <Form.Control.Feedback type="invalid">
                      {formik.errors.invoice_date}
                    </Form.Control.Feedback>
                  </Col>
                  <Col lg={3}>
                    <NewFileUploadInput
                      label="Upload Invoice"
                      name="upload_invoice"
                      formik={formik}
                    />
                  </Col>
                </Row>

                <hr />
              </>
            )}

            {!isAddStockOpen && <h5 className="fw-semibold mb-3">Products</h5>}

            <CreateGRNTable
              items={products}
              setItems={setProducts}
              isAddStockOpen={isAddStockOpen}
              setIsAddStockOpen={setIsAddStockOpen}
              formik={formik}
            />

            {!isAddStockOpen && (
              <>
                <hr />

                {/* Notes */}
                <Row className="mb-3 g-4">
                  <h5 className="fw-semibold">Notes</h5>
                  <Col md={12}>
                    <Accordion
                      defaultActiveKey="0"
                      className="mb-3 accordion-notes"
                    >
                      <Accordion.Item eventKey="0">
                        <Accordion.Header>Notes</Accordion.Header>
                        <Accordion.Body className="p-0">
                          <Form.Group className="mb-0">
                            <Form.Control
                              as="textarea"
                              rows={4}
                              name="notes"
                              placeholder="Enter notes"
                              value={formik.values.notes || ""}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              isInvalid={
                                formik.touched.notes && !!formik.errors.notes
                              }
                            />

                            <Form.Control.Feedback type="invalid">
                              {formik.touched.notes && formik.errors.notes}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Accordion.Body>
                      </Accordion.Item>
                    </Accordion>
                  </Col>
                </Row>

                <hr />

                {/* Attach Files */}
                <Row className="mt-3 g-4">
                  {/* <h5 className="fw-semibold">Attachments</h5> */}

                  <FileUpload
                    label={"Attachments"}
                    multiple={true}
                    value={formik.values.attachments}
                    onChange={(files) =>
                      formik.setFieldValue("attachments", files)
                    }
                  />
                </Row>

                {/* Cancel & Submit Update Buttons */}
                <div className="d-flex flex-column flex-md-row justify-content-center gap-3 mt-3">
                  <CancelButton
                    redirectTo="/warehouse-item-inward"
                    disabled={isSubmitting}
                  />
                  <PrimarySubmitButton
                    label={false ? "Update" : "Submit"}
                    isLoading={isSubmitting}
                    loadingLabel={false ? "Updating..." : "Submitting..."}
                  />
                </div>
              </>
            )}
          </Form>
        )}
      </Container>
    </div>
  );
};

export default WarehouseCreateGRN;
